import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Distributor business information state
 * Stores company details, legal structure, and contact information for distributor onboarding
 */
export interface DisBusinessState {
  // Company identification
  company: string | null;
  regNumber: string | null;
  dateOfIncorporation: string | null;

  // Legal structure
  legalStructure: string | null;
  legalStructureOthers: string | null;

  // Industry classification
  industryGroup: string | null;
  industryDropdownValues: any | null; // TODO: Define proper type for industry dropdown values
  industryOthers: string | null;
  typeOfInstitute: string | null;

  // Location details
  countryISOCode: string | null;
  address: string | null;
  postal: string | null;
  website: string | null;

  // Primary contact
  userFirstName: string | null;
  userLastName: string | null;
}

const initialState: DisBusinessState = {
  company: null,
  regNumber: null,
  dateOfIncorporation: null,
  legalStructure: null,
  legalStructureOthers: null,
  industryGroup: null,
  industryDropdownValues: null,
  typeOfInstitute: null,
  industryOthers: null,
  countryISOCode: null,
  address: null,
  postal: null,
  website: null,
  userFirstName: null,
  userLastName: null,
};

/**
 * Generic reducer factory for simple field updates
 * Eliminates duplication for setters that just assign a value
 */
const createFieldSetter = <K extends keyof DisBusinessState>(field: K) => {
  return (state: DisBusinessState, action: PayloadAction<DisBusinessState[K]>) => {
    state[field] = action.payload;
  };
};

const bizDisSlice = createSlice({
  name: "disBiz",
  initialState,
  reducers: {
    // Company identification
    setCompanyRedux: createFieldSetter("company"),
    setRegNumberRedux: createFieldSetter("regNumber"),
    setDateOfIncorporationRedux: createFieldSetter("dateOfIncorporation"),

    // Legal structure
    setLegalStructureRedux: createFieldSetter("legalStructure"),
    setLegalStructureOthersRedux: createFieldSetter("legalStructureOthers"),
    setTypeOfInstituteRedux: createFieldSetter("typeOfInstitute"),

    // Industry classification
    setIndustryGroupRedux: createFieldSetter("industryGroup"),
    setIndustryDropdownValuesRedux: createFieldSetter("industryDropdownValues"),
    setIndustryOthersRedux: createFieldSetter("industryOthers"),

    // Location and contact
    setCountryISOCodeRedux: createFieldSetter("countryISOCode"),
    setAddressRedux: createFieldSetter("address"),
    setPostalRedux: createFieldSetter("postal"),
    setWebsiteRedux: createFieldSetter("website"),

    // Primary contact
    setUserFirstNameRedux: createFieldSetter("userFirstName"),
    setUserLastNameRedux: createFieldSetter("userLastName"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateDisBizState: (state, action: PayloadAction<Partial<DisBusinessState>>) => {
      return { ...state, ...action.payload };
    },

    /**
     * Reset to initial state
     */
    resetDisBizState: () => initialState,
  },
});

export const {
  setCompanyRedux,
  setRegNumberRedux,
  setDateOfIncorporationRedux,
  setLegalStructureRedux,
  setLegalStructureOthersRedux,
  setIndustryGroupRedux,
  setIndustryDropdownValuesRedux,
  setTypeOfInstituteRedux,
  setIndustryOthersRedux,
  setCountryISOCodeRedux,
  setPostalRedux,
  setAddressRedux,
  setWebsiteRedux,
  setUserFirstNameRedux,
  setUserLastNameRedux,
  hydrateDisBizState,
  resetDisBizState,
} = bizDisSlice.actions;

export default bizDisSlice.reducer;
